import type { CreatorApplication } from "@/types/creatorNetwork";

// Matches the corporate-strapi kol-submission-form-data schema exactly.
const NAME_REGEX = /^[a-zA-Z\s/-]+$/;
const PHONE_REGEX = /^(\+?6?01)[0-46-9]-*[0-9]{7,8}$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/;
const URL_REGEX = /^https?:\/\/.+\..+/i;

export type FieldErrors = Record<string, string | null | undefined>;

export function hasErrors(errors: FieldErrors) {
  return Object.values(errors).some((e) => !!e);
}

export function validateAbout(data: CreatorApplication): FieldErrors {
  const errors: FieldErrors = {};

  if (!data.fullName.trim()) errors.fullName = "Please enter your full name";
  else if (!NAME_REGEX.test(data.fullName))
    errors.fullName = "Name can only contain letters, spaces, / and -";
  if (!data.mobile.trim()) errors.mobile = "Please enter your mobile number";
  else if (!PHONE_REGEX.test(data.mobile))
    errors.mobile = "Enter a valid Malaysian mobile number (e.g. 012-3456789)";
  if (!data.email.trim()) errors.email = "Please enter your email";
  else if (!EMAIL_REGEX.test(data.email)) errors.email = "Enter a valid email address";
  if (!data.stateRegion) errors.stateRegion = "Please select your state/country";
  if (!data.ageRange) errors.ageRange = "Please select an age range";
  if (!data.gender) errors.gender = "Please select a gender";
  if (!data.occupation) errors.occupation = "Please select an occupation";
  else if (data.occupation === "others" && !data.occupationOther.trim())
    errors.occupation = "Please tell us your occupation";
  if (data.languages.length === 0) errors.languages = "Select at least one language";
  else if (data.languages.includes("others") && !data.languagesOther.trim())
    errors.languages = "Please tell us which other language";

  return errors;
}

export function validateSocials(data: CreatorApplication): FieldErrors {
  const errors: FieldErrors = {};
  const primary = data.socialProfiles[0];

  if (!primary) {
    errors.socials = "Add at least one platform";
    return errors;
  }

  if (!primary.platform) errors.platform = "Select a platform";
  if (!primary.handle.trim()) errors.handle = "Enter your handle or profile URL";
  if (!primary.followerRange) errors.followerRange = "Select a follower range";

  // Rate card disabled for now — no validation required while hidden
  // if (!primary.noRatesYet) {
  //   if (primary.rates.length === 0) {
  //     errors.rates = "Add at least one rate, or tick “I don’t have set rates yet”";
  //   } else {
  //     const incomplete = primary.rates.some(
  //       (r) =>
  //         !r.postType ||
  //         r.minAmount === null ||
  //         r.maxAmount === null ||
  //         r.minAmount < 0 ||
  //         r.maxAmount < r.minAmount
  //     );
  //     if (incomplete) errors.rates = "Complete every rate row (min must not exceed max)";
  //   }
  // }

  const firstLink = primary.contentLinks[0]?.trim();
  if (firstLink && !URL_REGEX.test(firstLink))
    errors.contentLinks = "Enter a valid link starting with http:// or https://";

  const invalidExtraLink = primary.contentLinks
    .slice(1)
    .some((link) => link.trim() && !URL_REGEX.test(link.trim()));
  if (invalidExtraLink) errors.contentLinks = "Enter valid links, or leave them blank";

  const incompleteOther = data.socialProfiles
    .slice(1)
    .some((p) => p.platform && (!p.handle.trim() || !p.followerRange));
  if (incompleteOther)
    errors.socials = "Fill in the handle and follower range for every platform added";

  return errors;
}

export function validateContent(data: CreatorApplication): FieldErrors {
  const errors: FieldErrors = {};

  if (data.interests.length === 0) errors.interests = "Select at least one interest";
  else if (data.interests.includes("others") && !data.interestsOther.trim())
    errors.interests = "Tell us what else you create about";
  if (data.styles.length === 0) errors.styles = "Select at least one content style";
  if (data.showFace === null) errors.showFace = "Please choose an option";
  if (data.ugcOnly === null) errors.ugcOnly = "Please choose an option";

  return errors;
}

export function validatePreferences(data: CreatorApplication): FieldErrors {
  const errors: FieldErrors = {};

  if (data.campaignTypes.length === 0)
    errors.campaignTypes = "Select at least one campaign type";
  if (!data.compensation) errors.compensation = "Please choose an option";
  if (data.attendEvents === null) errors.attendEvents = "Please choose an option";
  if (data.attendEvents) {
    if (data.areas.length === 0) errors.areas = "Select at least one area";
    else if (data.areas.includes("others") && !data.areasOther.trim())
      errors.areas = "Please tell us which other area";
  }

  return errors;
}

export function validateJoin(data: CreatorApplication): FieldErrors {
  const errors: FieldErrors = {};

  if (!data.whyJoin.trim() || data.whyJoin.trim().length < 10)
    errors.whyJoin = "Tell us a little more (at least 10 characters)";
  if (!data.howDidYouHearAboutUs)
    errors.howDidYouHearAboutUs = "Please select an option";
  else if (
    data.howDidYouHearAboutUs === "others" &&
    !data.howDidYouHearAboutUsOther.trim()
  )
    errors.howDidYouHearAboutUs = "Please tell us how you heard about us";
  if (!data.consentContact) errors.consentContact = "Required to proceed";
  if (!data.consentPdpa) errors.consentPdpa = "Required to proceed";

  return errors;
}

export const STEP_VALIDATORS = [
  validateAbout,
  validateSocials,
  validateContent,
  validatePreferences,
  validateJoin,
];
